import { z } from 'zod';
import { isDateKey, todayDateKey } from '@features/quote/utils/dateKey';

export const NO_SERVICE_OPTION = 'none';
export const AUTO_MATCH_OPTION = 'auto';
export const MAX_SERVICE_MILEAGE = 1_000_000;
const SERVICE_OPTION_PREFIX = 'option:';

export const serviceOptionValue = (id: string): string => `${SERVICE_OPTION_PREFIX}${id}`;

export const serviceOptionIdFrom = (value: string): string | null =>
    value.startsWith(SERVICE_OPTION_PREFIX) ? value.slice(SERVICE_OPTION_PREFIX.length) : null;

const isListedOption = (value: string, optionIds: readonly string[]): boolean => {
    const id = serviceOptionIdFrom(value);
    return id !== null && optionIds.includes(id);
};

const mileageText = z
    .string()
    .trim()
    .superRefine((value, ctx) => {
        if (value === '') return;
        const mileage = Number(value);
        if (!Number.isInteger(mileage)) {
            ctx.addIssue({ code: 'custom', message: 'Mileage must be a whole number' });
        } else if (mileage < 0) {
            ctx.addIssue({ code: 'custom', message: 'Mileage must be at least 0' });
        } else if (mileage > MAX_SERVICE_MILEAGE) {
            ctx.addIssue({
                code: 'custom',
                message: `Mileage must be at most ${MAX_SERVICE_MILEAGE.toLocaleString('en-GB')}`,
            });
        }
    });

const dateText = (maxDate: string) =>
    z
        .string()
        .trim()
        .superRefine((value, ctx) => {
            if (value === '') return;
            if (!isDateKey(value)) {
                ctx.addIssue({ code: 'custom', message: 'Enter a valid date' });
            } else if (value > maxDate) {
                const message =
                    maxDate < todayDateKey()
                        ? 'The service date cannot be after the plan start date'
                        : 'The service date cannot be in the future';
                ctx.addIssue({ code: 'custom', message });
            }
        });

export const smriqLastCompletedServiceSchema = (optionIds: readonly string[], maxDate: string) =>
    z
        .object({
            service_option_id: z
                .string()
                .refine(
                    (value) =>
                        value === NO_SERVICE_OPTION ||
                        value === AUTO_MATCH_OPTION ||
                        isListedOption(value, optionIds),
                    'Choose one of the listed services',
                ),
            completed_date: dateText(maxDate),
            completed_mileage: mileageText,
        })
        .superRefine((value, ctx) => {
            if (value.service_option_id === NO_SERVICE_OPTION) return;
            if (value.completed_date === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['completed_date'],
                    message: 'Date completed is required',
                });
            }
            if (value.completed_mileage === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['completed_mileage'],
                    message: 'Mileage at service is required',
                });
            }
        });

export type SmriqLastCompletedServiceFormValues = z.input<
    ReturnType<typeof smriqLastCompletedServiceSchema>
>;
